import React, { useState, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  FileText,
  Sparkles,
  Building2,
  Code,
  Cpu,
  Globe,
  Shield,
  Zap,
  MessageSquare,
} from 'lucide-react';
import { LocalizedLink } from './LocalizedLink';
import { cn } from '@/lib/utils';
import { supportedLanguages, type SupportedLanguage } from '@/i18n';
import { localizedRoutes, findRouteKeyFromPath } from '@/i18n/routes';

const languageNames: Record<SupportedLanguage, string> = {
  it: 'IT',
  en: 'EN',
  es: 'ES',
  de: 'DE',
  fr: 'FR',
};

interface LanguageSelectorDropdownProps {
  currentLang: SupportedLanguage;
  onLanguageChange: (lang: SupportedLanguage) => void;
}

function LanguageSelectorDropdown({ currentLang, onLanguageChange }: LanguageSelectorDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        className={cn(
          'flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all w-16 cursor-pointer',
          isOpen
            ? 'bg-primary-50 text-primary-400'
            : 'text-gray-700 hover:text-primary-400 hover:bg-gray-50'
        )}
      >
        <span className="w-6 text-center">{languageNames[currentLang]}</span>
        <ChevronDown
          className={cn('w-4 h-4 transition-transform flex-shrink-0', isOpen && 'rotate-180')}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 pt-2 w-24 z-[100]"
          >
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="py-1">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => onLanguageChange(lang)}
                  className={cn(
                    'w-full px-4 py-2 text-center text-sm font-medium transition-colors cursor-pointer',
                    lang === currentLang
                      ? 'bg-primary-50 text-primary-400'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  {languageNames[lang]}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
}

interface NavChild {
  label: string;
  route: string;
  badge?: string;
  icon: React.ComponentType<{ className?: string }>;
  colorTheme: 'orange' | 'green' | 'pink';
}

const products: NavChild[] = [
  { label: 'FirmoSEMPLICE', route: 'product_firmo', badge: 'FirmaSPID', icon: FileText, colorTheme: 'orange' },
  { label: 'SempliceAI', route: 'product_ai', badge: '01Semplice', icon: Sparkles, colorTheme: 'green' },
  { label: 'SemplicePA', route: 'product_pa', badge: '01Semplice', icon: Building2, colorTheme: 'green' },
];

const getServices = (t: any): NavChild[] => [
  { label: t('home.services.dev.title'), route: 'service_dev', icon: Code, colorTheme: 'pink' },
  { label: t('home.services.integration.title'), route: 'service_integration', icon: Cpu, colorTheme: 'pink' },
  { label: t('home.services.transformation.title'), route: 'service_transformation', icon: Globe, colorTheme: 'pink' },
  { label: t('home.services.security.title'), route: 'service_security', icon: Shield, colorTheme: 'pink' },
  { label: t('home.services.cloud.title'), route: 'service_cloud', icon: Zap, colorTheme: 'pink' },
  { label: t('home.services.consulting.title'), route: 'service_consulting', icon: MessageSquare, colorTheme: 'pink' },
];

// Color theme configurations
const colorThemes = {
  orange: {
    bg: 'bg-cta-100',
    bgHover: 'group-hover:bg-cta-200',
    text: 'text-cta-500',
    textHover: 'group-hover:text-cta-500',
    border: 'border-cta-100',
    title: 'text-cta-500',
  },
  green: {
    bg: 'bg-accent-100',
    bgHover: 'group-hover:bg-accent-200',
    text: 'text-accent-500',
    textHover: 'group-hover:text-accent-500',
    border: 'border-accent-100',
    title: 'text-accent-500',
  },
  pink: {
    bg: 'bg-primary-100',
    bgHover: 'group-hover:bg-primary-200',
    text: 'text-primary-400',
    textHover: 'group-hover:text-primary-400',
    border: 'border-primary-100',
    title: 'text-primary-400',
  },
};

export const Header = memo(function Header() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const currentLang = (i18n.language || 'it') as SupportedLanguage;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Blocca lo scroll quando il menu mobile è aperto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking inside desktop nav dropdown
      if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
        return;
      }
      // Don't close if clicking inside mobile menu
      if (mobileMenuRef.current && mobileMenuRef.current.contains(event.target as Node)) {
        return;
      }
      setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentPath = location.pathname.split('/').slice(2).join('/') || '';
  const currentRouteKey = findRouteKeyFromPath(currentPath, currentLang) || 'home';
  const isActive = (route: string) => route === currentRouteKey;

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) return;

    // Salva la posizione di scroll prima del cambio
    const scrollY = window.scrollY;

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const currentPage = pathSegments.slice(1).join('/');

    let newPath = '';
    for (const [, routes] of Object.entries(localizedRoutes)) {
      if (routes[currentLang] === currentPage) {
        newPath = routes[newLang];
        break;
      }
    }

    if (!newPath && currentPage) {
      newPath = currentPage;
    }

    i18n.changeLanguage(newLang);
    navigate(`/${newLang}${newPath ? `/${newPath}` : ''}`);

    // Ripristina la posizione di scroll dopo la navigazione
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  const { t } = useTranslation();
  const services = getServices(t);

  const navLinks = [
    { label: t('nav.home'), route: 'home' },
    { label: t('nav.about'), route: 'about' },
    { label: t('nav.solutions'), route: '', hasDropdown: true },
    { label: t('nav.realizzazioni'), route: 'realizzazioni' },
    { label: 'FAQ', route: 'faq' },
    { label: t('nav.contacts'), route: 'contacts' },
  ];

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        )}
        style={{ height: '80px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <LocalizedLink to="home" className="flex-shrink-0">
              <img
                src="/media/logos/spl-logo1.png"
                alt="01Sistemi"
                className="h-8 lg:h-10 w-auto"
              />
            </LocalizedLink>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
              {navLinks.map((link) => (
                <div key={link.label} className="relative z-50">
                  {link.hasDropdown ? (
                    <div
                      onMouseEnter={() => setOpenDropdown('soluzioni')}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button
                        className={cn(
                          'flex items-center justify-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors min-w-[100px] cursor-pointer',
                          openDropdown === 'soluzioni'
                            ? 'text-primary-400 bg-primary-50'
                            : 'text-gray-700 hover:text-primary-400 hover:bg-gray-50'
                        )}
                      >
                        <span className="text-center whitespace-nowrap">{link.label}</span>
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 transition-transform flex-shrink-0',
                            openDropdown === 'soluzioni' && 'rotate-180'
                          )}
                        />
                      </button>

                      {/* Dropdown */}
                      <AnimatePresence>
                        {openDropdown === 'soluzioni' && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-0 pt-2 z-[100]"
                          >
                            <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-[480px]">
                              <div className="grid grid-cols-2 gap-4">
                                {/* Prodotti */}
                                <div>
                                  <div className="px-2 py-1 text-xs font-bold uppercase tracking-wider border-b border-gray-200 mb-2 bg-gradient-to-r from-cta-500 to-accent-500 bg-clip-text text-transparent">
                                    {t('home.productsSection.title')}
                                  </div>
                                  <div className="space-y-1">
                                    {products.map((item) => {
                                      const Icon = item.icon;
                                      const theme = colorThemes[item.colorTheme];
                                      return (
                                        <LocalizedLink
                                          key={item.route}
                                          to={item.route as any}
                                          className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                                        >
                                          <div className={`w-8 h-8 rounded-lg ${theme.bg} flex items-center justify-center ${theme.bgHover}`}>
                                            <Icon className={`w-4 h-4 ${theme.text}`} />
                                          </div>
                                          <div>
                                            <div className={`text-sm font-medium text-gray-900 ${theme.textHover}`}>
                                              {item.label}
                                            </div>
                                            {item.badge && (
                                              <div className="text-[10px] text-gray-500">{t('home.products.firmo.badge').includes('di ') ? t('home.products.firmo.badge').split(' ')[0] : 'by'} {item.badge}</div>
                                            )}
                                          </div>
                                        </LocalizedLink>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Servizi */}
                                <div>
                                  <div className="px-2 py-1 text-xs font-bold text-primary-400 uppercase tracking-wider border-b border-primary-100 mb-2">
                                    {t('home.servicesSection.title')}
                                  </div>
                                  <div className="space-y-1">
                                    {services.map((item) => {
                                      const Icon = item.icon;
                                      const theme = colorThemes[item.colorTheme];
                                      return (
                                        <LocalizedLink
                                          key={item.route}
                                          to={item.route as any}
                                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-primary-50 transition-colors group"
                                        >
                                          <div className={`w-8 h-8 rounded-lg ${theme.bg} flex items-center justify-center ${theme.bgHover}`}>
                                            <Icon className={`w-4 h-4 ${theme.text}`} />
                                          </div>
                                          <span className={`text-sm font-medium text-gray-900 ${theme.textHover}`}>
                                            {item.label}
                                          </span>
                                        </LocalizedLink>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <LocalizedLink
                      to={link.route as any}
                      className={cn(
                        'flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors relative min-w-[80px] text-center',
                        isActive(link.route)
                          ? 'text-primary-400 bg-primary-50'
                          : 'text-gray-700 hover:text-primary-400 hover:bg-gray-50'
                      )}
                    >
                      <span className="whitespace-nowrap">{link.label}</span>
                      {isActive(link.route) && (
                        <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
                      )}
                    </LocalizedLink>
                  )}
                </div>
              ))}

              {/* Language Selector Dropdown */}
              <div className="ml-2 pl-2 border-l border-gray-200">
                <LanguageSelectorDropdown
                  currentLang={currentLang}
                  onLanguageChange={switchLanguage}
                />
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-3 rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div style={{ height: '80px' }} />

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-gray-900/50"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              ref={mobileMenuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="absolute right-0 top-0 bottom-0 w-[280px] sm:w-80 max-w-[85vw] bg-white shadow-xl"
            >
              <div className="p-4 pt-20 space-y-2">
                {navLinks.map((link) => (
                  <div key={link.label}>
                    {link.hasDropdown ? (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === 'mobile-sol' ? null : 'mobile-sol');
                          }}
                          className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
                        >
                          <span className="font-medium">{link.label}</span>
                          <ChevronDown
                            className={cn(
                              'w-5 h-5 transition-transform',
                              openDropdown === 'mobile-sol' && 'rotate-180'
                            )}
                          />
                        </button>
                        <AnimatePresence>
                          {openDropdown === 'mobile-sol' && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: 'auto' }}
                              exit={{ height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 py-2 space-y-3 max-h-[60vh] overflow-y-auto">
                                <div>
                                  <div className="px-4 py-1 text-xs font-bold uppercase bg-gradient-to-r from-cta-500 to-accent-500 bg-clip-text text-transparent">{t('home.productsSection.title')}</div>
                                  {products.map((item) => {
                                    const theme = colorThemes[item.colorTheme];
                                    return (
                                      <LocalizedLink
                                        key={item.route}
                                        to={item.route as any}
                                        className={`flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg`}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                      >
                                        <div className={`w-6 h-6 rounded ${theme.bg} flex items-center justify-center`}>
                                          <item.icon className={`w-3 h-3 ${theme.text}`} />
                                        </div>
                                        {item.label}
                                      </LocalizedLink>
                                    );
                                  })}
                                </div>
                                <div>
                                  <div className="px-4 py-1 text-xs font-bold text-primary-400 uppercase">{t('home.servicesSection.title')}</div>
                                  {services.map((item) => {
                                    const theme = colorThemes[item.colorTheme];
                                    return (
                                      <LocalizedLink
                                        key={item.route}
                                        to={item.route as any}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-primary-50 rounded-lg"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                      >
                                        <div className={`w-6 h-6 rounded ${theme.bg} flex items-center justify-center`}>
                                          <item.icon className={`w-3 h-3 ${theme.text}`} />
                                        </div>
                                        {item.label}
                                      </LocalizedLink>
                                    );
                                  })}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <LocalizedLink
                        to={link.route as any}
                        className={cn(
                          'block px-4 py-3 rounded-lg font-medium',
                          isActive(link.route)
                            ? 'text-primary-400 bg-primary-50'
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.label}
                      </LocalizedLink>
                    )}
                  </div>
                ))}
                {/* Language Selector Mobile */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="px-4 pb-2 text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Language
                  </div>
                  <div className="flex gap-2 px-4">
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          switchLanguage(lang);
                          setIsMobileMenuOpen(false);
                        }}
                        className={cn(
                          'flex-1 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer',
                          lang === currentLang
                            ? 'bg-primary-400 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        {languageNames[lang]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});
